import React, { useEffect, useRef, useState } from 'react';
import { IonButton } from '@ionic/react';
import { useHistory } from 'react-router';
import { requestService, whatsappCampaignContext, whatsappCampaignConsent } from '../../../../../Utils/Services';

export type OfficialTemplate = { key: string; version: number } | null;
export type CampaignContext = { activeChannel: string; preference: 'text' | 'template'; consented: boolean; canEdit: boolean; connected?: boolean;
  catalog: any[]; templates: any[]; nextCursor?: string };
export function campaignPresentation(context: CampaignContext | null, value: OfficialTemplate, hasImage: boolean, coupon: string) {
  const templateMode = context?.activeChannel === 'meta_cloud_api' && context.preference === 'template';
  const selected = context?.catalog.find(item => item.key === value?.key && item.version === value?.version);
  const remote = context?.templates.find(item => item.name === selected?.name && item.language === selected?.language);
  const needsImage = templateMode && selected?.headerFormat === 'IMAGE';
  let configurationError = '';
  if (templateMode) {
    if (!selected) configurationError = 'Pendente de configuração: selecione um template para enviar pelo Oficial.';
    else if (needsImage && !hasImage) configurationError = 'Adicione a imagem exigida pelo template.';
    else if (!needsImage && hasImage) configurationError = 'Este template não usa imagem. Remova a imagem associada ou escolha a variante com imagem.';
    else if (selected.variables.some((variable: any) => variable.field === 'coupon') && !coupon.trim()) configurationError = 'Informe o cupom na etapa 4.';
  }
  const statusText = ({ APPROVED: 'Aprovado pela Meta', PENDING: 'Em análise pela Meta — ainda não pode ser enviado',
    REJECTED: 'Rejeitado pela Meta — não pode ser enviado', PAUSED: 'Pausado pela Meta — não pode ser enviado',
    DISABLED: 'Desativado pela Meta — não pode ser enviado' } as Record<string, string>)[remote?.status]
    || (remote ? 'Status não disponível para envio' : 'Não cadastrado na Meta — ainda não pode ser enviado');
  let activationError = !context ? 'Aguarde a consulta do formato de envio.' : '';
  if (context?.activeChannel === 'meta_cloud_api') {
    if (context.connected === false) activationError = 'Conecte o WhatsApp Oficial antes de ativar a campanha.';
    else if (configurationError) activationError = configurationError;
    else if (templateMode && remote?.status !== 'APPROVED') activationError = statusText;
    else if (templateMode && (!remote?.matchesCatalog || remote?.category !== 'MARKETING')) activationError = 'O template diverge do conteúdo ou da categoria esperados e não poderá ser enviado.';
    else if (!context.consented) activationError = 'Registre a autorização dos destinatários antes de ativar a campanha.';
  }
  return { templateMode, freeTextMode: !!context && !templateMode, selected, remote, needsImage, configurationError, activationError, statusText };
}
type Props = { empresaId: string; value: OfficialTemplate; onChange: (value: OfficialTemplate) => void;
  onContext: (context: CampaignContext | null) => void; hasImage: boolean; coupon: string; business: string; onRemoveImage: () => void };

const OfficialCampaignEditor: React.FC<Props> = props => {
  const history = useHistory();
  const [context, setContext] = useState<CampaignContext | null>(null);
  const [error, setError] = useState('');
  const [accepted, setAccepted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [reload, setReload] = useState(0);
  const generation = useRef(0);
  useEffect(() => {
    let live = true;
    generation.current += 1;
    setContext(null); props.onContext(null); setError(''); setAccepted(false); setBusy(false);
    const base = whatsappCampaignContext.url.replace('{empresaId}', encodeURIComponent(props.empresaId));
    const seen = new Set<string>();
    const load = (after = '', templates: any[] = []) => requestService(base + (after ? `?after=${encodeURIComponent(after)}` : ''),
      { method: 'GET', credentials: 'include' }, history, (result: any) => {
        if (!live) return;
        if (!Array.isArray(result?.catalog) || !Array.isArray(result?.templates)) { setError('Resposta inválida ao consultar o Oficial.'); return; }
        const all = [...templates, ...result.templates];
        if (result.nextCursor) {
          if (seen.has(result.nextCursor)) { setError('Não foi possível concluir a consulta dos templates.'); return; }
          seen.add(result.nextCursor); load(result.nextCursor, all); return;
        }
        const next = { ...result, templates: all }; setContext(next); props.onContext(next);
      }, () => { if (live) setError('Não foi possível consultar o WhatsApp Oficial. O envio validará novamente sua configuração.'); }, true);
    if (props.empresaId) load();
    return () => { live = false; generation.current += 1; };
    // onContext is the stable setter owned by the campaign form.
  }, [props.empresaId, history, reload, props.onContext]);
  const { selected, remote, templateMode, needsImage, configurationError, activationError, statusText } = campaignPresentation(context, props.value, props.hasImage, props.coupon);
  const parameter = (field: string) => ({ customer: 'Mariana', business: props.business || '[nome da empresa]',
    coupon: props.coupon || '[cupom obrigatório]', link: '[link do cardápio gerado pelo Xeguei]' } as Record<string, string>)[field];
  const preview = selected?.body.replace(/\{\{(\d+)\}\}/g, (_: string, index: string) => parameter(selected.variables[Number(index) - 1].field));
  const saveConsent = () => {
    if (!accepted || busy || !context?.canEdit) return;
    const current = generation.current;
    setBusy(true);
    requestService(whatsappCampaignConsent.url.replace('{empresaId}', encodeURIComponent(props.empresaId)),
      { method: 'POST', credentials: 'include', body: JSON.stringify({ accepted: true }) }, history,
      () => { if (current === generation.current) { setBusy(false); setReload(value => value + 1); } },
      () => { if (current === generation.current) { setBusy(false); setError('Não foi possível registrar a declaração.'); } }, true);
  };
  if (context?.activeChannel !== 'meta_cloud_api' && context) return null;
  return <section style={{ border: '1px solid #ffd4ad', borderRadius: 12, padding: 20, marginBottom: 20 }}>
    <h3>Campanha no WhatsApp Oficial</h3>
    {!context && !error && <p role="status">Consultando formato de envio...</p>}
    {error && <p role="alert">{error} <IonButton fill="clear" onClick={() => setReload(value => value + 1)}>Atualizar</IonButton></p>}
    {context && <>
      <p>Formato no Oficial: {templateMode ? 'envio por template (Marketing)' : 'texto livre'}. Sem troca automática de formato ou canal.</p>
      {templateMode && <IonButton fill="clear" onClick={() => setReload(value => value + 1)}>Atualizar status na Meta</IonButton>}
      {!context.consented && <>
        <label><input type="checkbox" checked={accepted} disabled={!context.canEdit || busy} onChange={event => setAccepted(event.target.checked)} />
          Declaro que possuo autorização dos destinatários para receber campanhas pelo WhatsApp.</label>
        <IonButton disabled={!accepted || !context.canEdit || busy} onClick={saveConsent}>Registrar declaração</IonButton>
        <p>Campanhas oficiais ficam bloqueadas até registrar essa declaração. Responder SAIR impede novas campanhas oficiais.</p>
      </>}
      {templateMode && <><label>Template oficial
        <select aria-label="Template oficial da campanha" value={props.value?.key || ''} disabled={!context.canEdit}
          style={{ display: 'block', width: '100%', padding: 12, marginTop: 8 }}
          onChange={event => { const item = context.catalog.find(entry => entry.key === event.target.value);
            props.onChange(item ? { key: item.key, version: item.version } : null); }}>
          <option value="">Selecione um modelo — não associado</option>
          {context.catalog.map(item => <option key={item.key} value={item.key}>{item.title}</option>)}
        </select>
      </label>
      {configurationError && <p role="alert">{configurationError}</p>}
      {props.hasImage && selected && !needsImage && <IonButton fill="outline" onClick={props.onRemoveImage}>Remover imagem associada</IonButton>}
      {selected && <>
        <p>{selected.name} · {remote?.category || 'MARKETING'} · {selected.language}</p>
        <p role="status">{statusText}</p>
        {remote?.status === 'APPROVED' && (!remote.matchesCatalog || remote.category !== 'MARKETING') && <p role="alert">O template diverge do conteúdo ou da categoria esperados e não poderá ser enviado.</p>}
        <h4>Prévia do template</h4>
        <p style={{ background: '#edf8f0', padding: 16, borderRadius: 12 }}>{preview}</p>
        <p>Prévia ilustrativa. No envio serão usados os dados reais. Texto fixo, sem sorteio de frases; aprovação e cobrança são definidas pela Meta.</p>
        <p>O cliente será preenchido por destinatário e o link do cardápio será gerado pelo Xeguei.</p>
      </>}
      {activationError && <p>Você pode salvar a configuração como pausada. A ativação exige resolver as pendências acima e registrar a autorização dos destinatários.</p>}
      </>}
    </>}
  </section>;
};
export default OfficialCampaignEditor;
